import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import 'moment-timezone';
import moment from 'moment';
import { compose } from 'utils/compose';
import DeckGL, { IconLayer } from 'deck.gl';
import { Map as ReactMapGL } from 'react-map-gl/maplibre';
import { Box, IconButton, Grid, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Close as CloseIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
} from '@mui/icons-material';

import { useStyles } from '../styles';
import ThumbUpFeedBack from './DialogThumbUp';
import withMediaQuery from 'constants/mediaQuery';
import PACKAGE_MARKERS from 'assets/svg/package_marker.svg';
import PreselectThumbUp from 'components/PreselectThumbUp';
import PreselectThumbDown from 'components/PreselectThumbDown';
import VerificationToken from 'components/VerificationToken';
import SelectPreferredPhoto from 'components/SelectPreferredPhoto';
import { DialogActionButton, DialogFooter } from 'components/Dialog';
import api from 'utils/api';

const mapStyle = `https://api.maptiler.com/maps/topo/style.json?key=${import.meta.env.REACT_APP_MAP_TILER_KEY}`;

function DialogFeedback({ store, mediaQuery, closeDialog, isDisplayPackage = true }) {
  const classes = useStyles();
  const [finishUpFeedback, setFinishUpFeedback] = useState(false);
  const [showPackageLocation, setShowPackageLocation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewport, setViewport] = useState({
    latitude: 37.772402,
    longitude: -122.414265,
    zoom: 15,
    maxZoom: 19,
    minZoom: 12,
  });
  const [images, setImages] = useState(store?.podStore?.imagesPOD);
  const [isShowSelectPhoto, setIsShowSelectPhoto] = useState(false);

  useEffect(() => {
    setImages(store?.podStore?.imagesPOD);
  }, [store?.podStore?.imagesPOD]);

  useEffect(() => {
    store.deliveryStore.getPackageLocation((res) => {
      if (res.ok) {
        setViewport({ ...viewport, ...res.data });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.deliveryStore]);

  const nextStep = () => {
    store.deliveryStore.setDialogErrorMsg('');
    setIsLoading(true);
    store.deliveryStore.processNextStep((res) => {
      setIsLoading(false);
      if (res.ok) {
        setIsShowSelectPhoto(true);
        store.podStore.updateField('isChanged', false);
        store.deliveryStore.updateField('isSubmittedFeedback', true);
      }
    });
  };

  const handleSubmit = () => {
    nextStep();
  };

  const handleChangeFeedbackData = (field, value) => {
    store.deliveryStore.setDialogErrorMsg('');
    const { feedback } = store.deliveryStore;

    if (field === 'thumb') {
      if (value === feedback.thumb) value = undefined;
      store.deliveryStore.setFeedbackField('tags', []);
    }

    if (field === 'tags') value = [value];

    store.deliveryStore.setFeedbackField(field, value);

    if (['thumb', 'tags'].includes(field)) {
      if (field !== 'thumb' || value !== undefined) {
        store.deliveryStore.submitFeedback();
      }
    }
  };

  const openManageDelivery = (e) => {
    e.preventDefault();
    window.open(`/${store.deliveryStore.trackingCode}?manage=true`, '_blank');
  };

  const renderMarker = (location) => {
    if (!location) return null;

    return new IconLayer({
      id: 'package-marker',
      data: [
        {
          location: [location.longitude, location.latitude],
          size: 40,
          icon: 'green',
        },
      ],
      pickable: true,
      iconAtlas: PACKAGE_MARKERS,
      iconMapping: {
        green: { x: 0, y: 0, width: 64, height: 83, anchorY: 83 },
      },
      getIcon: (d) => d.icon,
      sizeScale: 1,
      opacity: 1,
      visible: true,
      billboard: false,
      getPosition: (d) => d.location,
      getSize: (d) => d.size,
    });
  };

  const { deliveryStore } = store;
  const { delivery, feedback, packageLocation } = deliveryStore;
  if (!delivery) return null;

  const { shipment } = delivery;
  const timezone = shipment?.timezone ? shipment.timezone : 'America/Los_Angeles';
  let latestUpdated = null;
  let canSubmit = true;

  if (!!feedback._updated) {
    latestUpdated = moment(feedback._updated).tz(timezone).format('h:mm A, DD/MM/YYYY');
    canSubmit = moment().format('x') - moment(feedback._updated).format('x') > 30 * 1000; // 30s
  }

  const hasPackageLocation = packageLocation?.latitude && packageLocation.longitude;
  const layers = [renderMarker(packageLocation)];

  const handleClose = () => {
    if (store.deliveryStore.isSubmittedFeedback) {
      setFinishUpFeedback(true);
      return;
    }
    api
      .get(
        `/delivery/${deliveryStore.trackingCode}/${deliveryStore.delivery.shipment.id}/feedback/${deliveryStore.getLatestDropoffSuccess().stop_id}`,
      )
      .then((res) => deliveryStore.updateField('feedback', res.data || {}));
    closeDialog();
  };

  return (
    <Box>
      {store?.podStore?.currentStep < 4 && <VerificationToken store={store} closeDialog={closeDialog} setImages={(val) => setImages(val)} />}
      {store?.podStore?.currentStep === 4 && (
        <>
          <DialogTitle sx={classes.feedbackTitleContainer}>
            <Box py={2} sx={finishUpFeedback ? classes.feedbackTitleNoBottom : classes.feedbackTitle}>
              {!finishUpFeedback && (
                <Box component="strong" sx={classes.textOnSurfaceTextHeadline}>
                  Give us Feedback!
                </Box>
              )}
              <IconButton onClick={handleClose} aria-label="Cancel Feedback" sx={classes.dialogCloseButton} size="large">
                <CloseIcon color="inherit" fontSize="large" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={classes.rootDialogContent}>
            {finishUpFeedback && <ThumbUpFeedBack thumbValue={feedback.thumb} />}
            {!finishUpFeedback && !isShowSelectPhoto && (
              <Box py={1} sx={classes.feedbackContainer}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ ...classes.selfCenter, ...(mediaQuery.isMobile && classes.justifyContentCenter) }}>
                    <span>Was it a good delivery experience?</span>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 8 }} sx={{ ...classes.selfCenter, ...(mediaQuery.isMobile && classes.justifyContentCenter) }}>
                    <IconButton onClick={() => handleChangeFeedbackData('thumb', true)} size="large">
                      <ThumbUpIcon sx={feedback.thumb ? classes.greenIcon : undefined} />
                    </IconButton>
                    <IconButton onClick={() => handleChangeFeedbackData('thumb', false)} size="large">
                      <ThumbDownIcon sx={feedback.thumb === false ? classes.redIcon : undefined} />
                    </IconButton>
                  </Grid>
                  {feedback.thumb === true && (
                    <PreselectThumbUp
                      handleChange={(e) => handleChangeFeedbackData('tags', e.target.value)}
                      selectedValue={feedback?.tags?.[0]}
                      styles={
                        mediaQuery.isMobile && {
                          container: {
                            display: 'flex',
                            justifyContent: 'center',
                          },
                        }
                      }
                    />
                  )}
                  {feedback.thumb === false && (
                    <PreselectThumbDown
                      handleChange={(e) => handleChangeFeedbackData('tags', e.target.value)}
                      selectedValue={feedback?.tags?.[0]}
                      negativeFeedbacks={delivery?.settings?.negative_feedbacks}
                      styles={
                        mediaQuery.isMobile && {
                          container: {
                            display: 'flex',
                            justifyContent: 'center',
                          },
                        }
                      }
                    />
                  )}
                  {(feedback.thumb === true || feedback.thumb === false) && (
                    <>
                      <Grid size={{ xs: 12, sm: 1 }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          Other
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 11 }}>
                        <TextField
                          multiline
                          rows={5}
                          variant="outlined"
                          fullWidth
                          disabled={!canSubmit}
                          sx={classes.feedbackInput}
                          defaultValue={feedback.comment}
                          placeholder="Leave a comment here …"
                          onChange={(e) => handleChangeFeedbackData('comment', e.target.value)}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                {store.deliveryStore.dialogErrorMsg && (
                  <Box align="center" mt={1}>
                    <Typography color="error" variant="subtitle1">
                      {store.deliveryStore.dialogErrorMsg}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {isShowSelectPhoto && !finishUpFeedback && (
              <SelectPreferredPhoto closeDialog={closeDialog} setFinishUpFeedback={(val) => setFinishUpFeedback(val)} />
            )}
          </DialogContent>
          {!finishUpFeedback && !!images && isDisplayPackage && !(feedback.thumb === true || feedback.thumb === false) && (
            <Box px={3}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ ...classes.feedbackContainer, ...(mediaQuery.isMobile && classes.justifyContentCenter) }}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Delivery Photo(s)
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 8 }} sx={{ ...classes.selfCenter, ...(mediaQuery.isMobile && classes.justifyContentCenter) }}>
                  <Box display={'flex'} flexDirection={'row'} flexWrap="wrap" style={{ gap: '5px' }} justifyContent={mediaQuery.isMobile && 'center'}>
                    {images?.map((m, idx) => (
                      <img alt="" src={m?.url} key={`image-${idx}`} style={{ maxWidth: mediaQuery.isMobile ? '30%' : '15%' }} />
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {hasPackageLocation && (
                <Box my={1} py={1} sx={classes.underline} onClick={() => setShowPackageLocation(!showPackageLocation)}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Cannot find your package?
                  </Box>
                  {showPackageLocation ? <ArrowDropUpIcon sx={classes.arrowIcon} /> : <ArrowDropDownIcon sx={classes.arrowIcon} />}
                </Box>
              )}
              {showPackageLocation && (
                <Box sx={classes.textOnSurfaceTextTitle}>
                  <Box height={mediaQuery.isMobile ? 250 : 300} mt={2.5}>
                    <ReactMapGL
                      {...viewport}
                      dragRotate={false}
                      mapStyle={mapStyle}
                      onMove={(evt) => setViewport(evt.viewState)}
                      style={{ width: '100%', height: '100%' }}
                      attributionControl={false}
                    >
                      <DeckGL layers={layers} viewState={viewport} style={{ pointerEvents: 'none' }} />
                    </ReactMapGL>
                  </Box>
                  <p>This pin shows the location of where the package was delivered.</p>
                  <p>
                    <span>If it doesn't seem to be accurate, please proceed to our </span>
                    <a href="#" onClick={openManageDelivery}>
                      Manage Delivery
                    </a>
                    <span> feature to update your address or location pin for future deliveries.</span>
                  </p>
                </Box>
              )}
            </Box>
          )}
          {!finishUpFeedback && !isShowSelectPhoto && (feedback.thumb === true || feedback.thumb === false) && (
            <>
              {latestUpdated && (
                <Box sx={{ ...classes.grayText, ...classes.feedbackSubmittedTime }}>
                  <small>{`Submitted on ${latestUpdated}`}</small>
                </Box>
              )}
              <DialogFooter>
                {canSubmit ? (
                  <>
                    <DialogActionButton onClick={handleClose} variant="outlined" color="primary" disabled={isLoading}>
                      Cancel
                    </DialogActionButton>
                    <DialogActionButton color="primary" variant="contained" style={{ marginLeft: 5 }} onClick={handleSubmit} disabled={isLoading}>
                      Submit
                    </DialogActionButton>
                  </>
                ) : (
                  <Box>Thank you for your feedback!</Box>
                )}
              </DialogFooter>
            </>
          )}
        </>
      )}
    </Box>
  );
}

const DialogFeedbackCompose = compose(inject('store'), observer)(DialogFeedback);

export default withMediaQuery()(DialogFeedbackCompose);
